import type { SavedViewModelType } from 'src/api/generated';

/**
 * Browser-local saved-view selection scoped to one user and model.
 */
export interface SavedViewSelectionPreferenceScope {
  /** Current authenticated user identifier. */
  userId: number;
  /** Saved-view model currently rendered in the UI. */
  model: SavedViewModelType;
}

/**
 * Full browser-local storage payload for Communications saved-view selection.
 */
export interface SavedViewSelectionPreferenceStorage {
  /** User-scoped selected saved-view ids keyed by model. */
  scopes: Record<string, Record<string, number>>;
}

/**
 * Versioned storage key for Communications saved-view selection.
 */
export const SAVED_VIEW_SELECTION_PREFERENCE_STORAGE_KEY =
  'lp.ui.communications.savedViewSelection.v1';

/**
 * Custom event fired after saved-view selection updates in the current tab.
 */
export const SAVED_VIEW_SELECTION_PREFERENCE_CHANGE_EVENT =
  'lp.ui.communications.savedViewSelectionChange';

const createEmptyStorage = (): SavedViewSelectionPreferenceStorage => ({
  scopes: {},
});

const isValidPositiveInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

const normalizeStorage = (
  value: unknown
): SavedViewSelectionPreferenceStorage | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const rawScopes = candidate.scopes;

  if (!rawScopes || typeof rawScopes !== 'object') {
    return null;
  }

  const scopes: SavedViewSelectionPreferenceStorage['scopes'] = {};

  Object.entries(rawScopes as Record<string, unknown>).forEach(
    ([userId, rawModels]) => {
      if (!rawModels || typeof rawModels !== 'object') {
        return;
      }

      const models: Record<string, number> = {};

      Object.entries(rawModels as Record<string, unknown>).forEach(
        ([model, rawViewId]) => {
          if (!isValidPositiveInteger(rawViewId)) {
            return;
          }

          models[model] = rawViewId;
        }
      );

      if (Object.keys(models).length > 0) {
        scopes[userId] = models;
      }
    }
  );

  return { scopes };
};

/**
 * Safely reads the full Communications saved-view selection storage payload.
 *
 * @returns Normalized storage payload, or an empty storage object when missing
 * or invalid.
 */
export const readSavedViewSelectionPreferenceStorage =
  (): SavedViewSelectionPreferenceStorage => {
    if (typeof window === 'undefined') {
      return createEmptyStorage();
    }

    const rawValue = window.localStorage.getItem(
      SAVED_VIEW_SELECTION_PREFERENCE_STORAGE_KEY
    );

    if (!rawValue) {
      return createEmptyStorage();
    }

    try {
      const parsedValue = JSON.parse(rawValue) as unknown;
      return normalizeStorage(parsedValue) ?? createEmptyStorage();
    } catch {
      return createEmptyStorage();
    }
  };

const writeSavedViewSelectionPreferenceStorage = (
  storage: SavedViewSelectionPreferenceStorage
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    SAVED_VIEW_SELECTION_PREFERENCE_STORAGE_KEY,
    JSON.stringify(storage)
  );
  window.dispatchEvent(new Event(SAVED_VIEW_SELECTION_PREFERENCE_CHANGE_EVENT));
};

/**
 * Reads the stored saved-view id for one user/model scope.
 *
 * @param scope - Active user and saved-view model.
 * @returns Persisted saved-view id when one exists for the requested scope.
 */
export const readSavedViewSelectionPreference = (
  scope: SavedViewSelectionPreferenceScope
): number | null => {
  const storage = readSavedViewSelectionPreferenceStorage();
  const userKey = String(scope.userId);
  const hasUserScope = Object.prototype.hasOwnProperty.call(
    storage.scopes,
    userKey
  );
  const userScope = hasUserScope ? storage.scopes[userKey] : undefined;

  return userScope && scope.model in userScope ? userScope[scope.model] : null;
};

/**
 * Persists the selected saved-view id for one user/model scope.
 *
 * @param scope - Active user and saved-view model.
 * @param viewId - Selected saved-view id to persist.
 */
export const persistSavedViewSelectionPreference = (
  scope: SavedViewSelectionPreferenceScope,
  viewId: number
): void => {
  if (!isValidPositiveInteger(viewId)) {
    return;
  }

  const storage = readSavedViewSelectionPreferenceStorage();
  const userKey = String(scope.userId);
  const existingUserScope = storage.scopes[userKey];

  storage.scopes[userKey] = {
    ...existingUserScope,
    [scope.model]: viewId,
  };

  writeSavedViewSelectionPreferenceStorage(storage);
};

/**
 * Clears the selected saved-view id for one user/model scope only.
 *
 * @param scope - Active user and saved-view model.
 */
export const clearSavedViewSelectionPreference = (
  scope: SavedViewSelectionPreferenceScope
): void => {
  const storage = readSavedViewSelectionPreferenceStorage();
  const userKey = String(scope.userId);
  const hasUserScope = Object.prototype.hasOwnProperty.call(
    storage.scopes,
    userKey
  );

  if (!hasUserScope) {
    return;
  }

  const userScope = storage.scopes[userKey];

  if (!(scope.model in userScope)) {
    return;
  }

  const { [scope.model]: _, ...remainingModels } = userScope;

  if (Object.keys(remainingModels).length > 0) {
    storage.scopes[userKey] = remainingModels;
  } else {
    delete storage.scopes[userKey];
  }

  writeSavedViewSelectionPreferenceStorage(storage);
};
